import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDown } from '@/assets/icons';
import { Button } from '@/components/ui/button';
import { LANGUAGES } from '@/i18n/languages';
import { cn } from '@/lib/utils';

import { loadNotesHtml, resolveNotesLanguage, type NotesLoader } from './content';
import { RELEASE_NOTES, type ReleaseNotesEntry } from './manifest';

/** Typography for the rendered markdown (no typography plugin in this app). */
const NOTES_PROSE = cn(
    'text-sm text-foreground',
    '[&_h3]:mb-3 [&_h3]:text-base [&_h3]:font-semibold',
    '[&_h4]:mt-5 [&_h4]:mb-1.5 [&_h4]:text-sm [&_h4]:font-semibold',
    '[&_p]:my-2 [&_p]:text-muted-foreground',
    '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:text-muted-foreground',
    '[&_a]:underline [&_a]:underline-offset-2',
    '[&_img]:my-3 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-border',
    '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs',
);

/** "Sep 27, 2026" in the UI language. Dates are calendar dates, so format in
 *  UTC to avoid a timezone shifting them a day. */
export function formatReleaseDate(date: string, locale: string): string {
    try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
            new Date(`${date}T00:00:00Z`),
        );
    } catch {
        return date;
    }
}

function languageLabel(code: string): string {
    return LANGUAGES.find((l) => l.code === code)?.label ?? code;
}

interface ReleaseNotesListProps {
    /** Injectable for tests; defaults to the bundled manifest. */
    entries?: readonly ReleaseNotesEntry[];
    /** Injectable for tests; defaults to fetch + marked. */
    load?: NotesLoader;
}

/** Every release, newest first. The newest starts expanded; an entry fetches
 *  its notes the first time it expands. Shared by the dialog and the page. */
export function ReleaseNotesList({ entries = RELEASE_NOTES, load = loadNotesHtml }: ReleaseNotesListProps) {
    const { t } = useTranslation();
    if (entries.length === 0) {
        return <p className="text-sm text-muted-foreground">{t('whatsNew.empty')}</p>;
    }
    return (
        <div className="flex flex-col divide-y divide-border">
            {entries.map((entry, i) => (
                <ReleaseNotesEntryView key={entry.version} entry={entry} defaultOpen={i === 0} load={load} />
            ))}
        </div>
    );
}

type LoadState = { status: 'loading' } | { status: 'ready'; html: string } | { status: 'error' };

function ReleaseNotesEntryView({
    entry,
    defaultOpen,
    load,
}: {
    entry: ReleaseNotesEntry;
    defaultOpen: boolean;
    load: NotesLoader;
}) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<LoadState>({ status: 'loading' });
    const { language, fellBack } = resolveNotesLanguage(entry, i18n.language);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setState({ status: 'loading' });
        load(entry, language).then(
            (html) => {
                if (!cancelled) setState({ status: 'ready', html });
            },
            () => {
                if (!cancelled) setState({ status: 'error' });
            },
        );
        return () => {
            cancelled = true;
        };
    }, [open, entry, language, load, attempt]);

    const reactId = useId();
    const bodyId = `release-notes-v${entry.version}-${reactId}`;
    return (
        <section className="py-3 first:pt-0">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={open ? bodyId : undefined}
                onClick={() => setOpen((o) => !o)}
                className="flex w-full cursor-pointer items-center gap-2 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <span className="font-semibold">v{entry.version}</span>
                <span className="text-sm text-muted-foreground">
                    {formatReleaseDate(entry.date, i18n.language)}
                </span>
                <ChevronDown
                    aria-hidden
                    className={cn('ml-auto size-4 text-muted-foreground transition-transform', open && 'rotate-180')}
                />
            </button>
            {open && (
                <div id={bodyId} className="pt-2">
                    {fellBack && (
                        <p className="mb-2 text-xs text-muted-foreground">
                            {t('whatsNew.languageFallback', { language: languageLabel(i18n.language) })}
                        </p>
                    )}
                    {state.status === 'loading' && (
                        <p className="text-sm text-muted-foreground">{t('whatsNew.loading')}</p>
                    )}
                    {state.status === 'error' && (
                        <div className="flex items-center gap-3">
                            <p className="text-sm text-muted-foreground">{t('whatsNew.loadError')}</p>
                            <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>
                                {t('whatsNew.retry')}
                            </Button>
                        </div>
                    )}
                    {state.status === 'ready' && (
                        // Repo-authored markdown rendered by render-markdown.ts — trusted.
                        <div className={NOTES_PROSE} dangerouslySetInnerHTML={{ __html: state.html }} />
                    )}
                </div>
            )}
        </section>
    );
}
